import React, { useEffect, useMemo, useState } from 'react';
import {
  FileText,
  Plus,
  Pencil,
  Trash2,
  X,
  Eye,
  Loader2,
  Search,
  ChevronDown,
} from 'lucide-react';
import { ToastContainer, useToast } from '@/shared/components/ui/Toast';
import { CuteConfirmModal } from '@/shared/components/CuteConfirmModal';
import {
  getTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  listTemplateVariables,
  renderTemplate,
  EmailTemplate,
  TemplateVariable,
  CreateTemplateDto,
  getCategoryLabel,
} from '@/services/emailCrmService';
import { TemplateEditor } from './components/TemplateEditor';

type Category = 'MARKETING' | 'TRANSACTIONAL' | 'NOTIFICATION';
const CATEGORIES: Category[] = ['MARKETING', 'TRANSACTIONAL', 'NOTIFICATION'];

const SAMPLE_LEAD = {
  email: 'sample@nexedu.ma',
  firstName: 'Yasmine',
  lastName: 'Bennani',
  fullName: 'Yasmine Bennani',
  phone: '+212 6 12 34 56 78',
  company: 'Acme Education',
  jobTitle: 'Directrice générale',
  city: 'Casablanca',
  country: 'Maroc',
  customFields: { experience_years: 8, notes: 'Lead chaud' },
};

const emptyForm: CreateTemplateDto = {
  name: '',
  subject: '',
  content: '',
  previewText: '',
  category: 'MARKETING',
  primaryColor: '#4F46E5',
  secondaryColor: '#7C3AED',
};

export const EmailTemplatesPage: React.FC = () => {
  const { toasts, removeToast, success, error } = useToast();

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [variables, setVariables] = useState<TemplateVariable[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<Category | ''>('');

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateTemplateDto>(emptyForm);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  const [saving, setSaving] = useState(false);

  const [previewing, setPreviewing] = useState<EmailTemplate | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tpls, vars] = await Promise.all([getTemplates(), listTemplateVariables()]);
      setTemplates(tpls);
      setVariables(vars);
    } catch (err) {
      console.error(err);
      error('Erreur', 'Impossible de charger les templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      if (categoryFilter && t.category !== categoryFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          t.name.toLowerCase().includes(q) ||
          t.subject.toLowerCase().includes(q) ||
          (t.description ?? '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [templates, search, categoryFilter]);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setActiveTab('edit');
    setShowAdvanced(false);
    setShowFormModal(true);
  };

  const openEdit = (t: EmailTemplate) => {
    setEditingId(t.id);
    setFormData({
      name: t.name,
      description: t.description ?? '',
      subject: t.subject,
      content: t.content,
      previewText: t.previewText ?? '',
      headerImage: t.headerImage ?? '',
      primaryColor: t.primaryColor ?? '#4F46E5',
      secondaryColor: t.secondaryColor ?? '#7C3AED',
      footerText: t.footerText ?? '',
      category: (t.category as Category) ?? 'MARKETING',
    });
    setActiveTab('edit');
    setShowAdvanced(false);
    setShowFormModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return error('Nom requis', '');
    if (!formData.subject.trim()) return error('Objet requis', '');
    if (!formData.content.trim()) return error('Contenu requis', '');

    setSaving(true);
    try {
      if (editingId) {
        await updateTemplate(editingId, formData);
        success('Template modifié', '');
      } else {
        await createTemplate(formData);
        success('Template créé', '');
      }
      setShowFormModal(false);
      fetchAll();
    } catch (err: any) {
      error('Erreur', err?.message ?? 'Échec de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (t: EmailTemplate) => setDeleteTarget(t);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTemplate(deleteTarget.id);
      success('Supprimé', '');
      fetchAll();
    } catch (err: any) {
      error('Erreur', err?.message ?? 'Suppression impossible');
    } finally {
      setDeleteTarget(null);
    }
  };

  const previewSubject = useMemo(
    () => renderTemplate(formData.subject, { lead: SAMPLE_LEAD, unsubscribeUrl: '#' }),
    [formData.subject],
  );
  const previewBody = useMemo(
    () => renderTemplate(formData.content, { lead: SAMPLE_LEAD, unsubscribeUrl: '#' }),
    [formData.content],
  );

  return (
    <>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Templates d'email</h1>
            <p className="text-sm text-gray-500 mt-1">
              Modèles réutilisables pour vos campagnes marketing et automatisations
            </p>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            <Plus size={16} />
            Nouveau template
          </button>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un template…"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as Category | '')}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white"
          >
            <option value="">Toutes catégories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{getCategoryLabel(c)}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-indigo-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
            <FileText className="mx-auto mb-3 text-gray-300" size={48} />
            <p className="text-gray-500">Aucun template pour le moment.</p>
            <button
              onClick={openCreate}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100"
            >
              <Plus size={16} />
              Créer le premier
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((t) => (
              <div
                key={t.id}
                className="bg-white rounded-xl border border-gray-100 hover:shadow-md transition p-5"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{t.name}</h3>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded bg-indigo-50 text-indigo-700">
                      {getCategoryLabel(t.category)}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-1 line-clamp-1">
                  <strong>Objet:</strong> {t.subject}
                </p>
                {t.description && (
                  <p className="text-xs text-gray-500 line-clamp-2 mb-2">{t.description}</p>
                )}
                <div className="flex justify-end gap-1 pt-3 border-t border-gray-100 mt-3">
                  <button
                    onClick={() => setPreviewing(t)}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded"
                    title="Aperçu"
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    onClick={() => openEdit(t)}
                    className="p-2 text-indigo-600 hover:bg-indigo-50 rounded"
                    title="Modifier"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => askDelete(t)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    title="Supprimer"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showFormModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-5xl max-h-[92vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? 'Modifier le template' : 'Nouveau template'}
              </h2>
              <button
                onClick={() => setShowFormModal(false)}
                className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                  <input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Bienvenue prospect"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                  <select
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value as Category })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{getCategoryLabel(c)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Objet de l'email *
                </label>
                <input
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Ex: Bienvenue {{firstName}} chez NEXEDU"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('edit')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 ${
                    activeTab === 'edit'
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Édition
                </button>
                <button
                  onClick={() => setActiveTab('preview')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 ${
                    activeTab === 'preview'
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Aperçu
                </button>
              </div>

              {activeTab === 'edit' ? (
                <TemplateEditor
                  value={formData.content}
                  onChange={(html) => setFormData((f) => ({ ...f, content: html }))}
                  variables={variables}
                />
              ) : (
                <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
                  <p className="text-xs text-gray-500 mb-2">
                    Aperçu avec un lead exemple (Yasmine Bennani)
                  </p>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-500 mb-2 pb-2 border-b border-gray-100">
                      <strong>Objet :</strong> {previewSubject}
                    </p>
                    <div
                      className="prose prose-sm max-w-none"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: previewBody }}
                    />
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
              >
                <ChevronDown
                  size={16}
                  className={`transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                />
                Options avancées
              </button>
              {showAdvanced && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (interne)
                    </label>
                    <input
                      value={formData.description ?? ''}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Texte de prévisualisation
                    </label>
                    <input
                      value={formData.previewText ?? ''}
                      onChange={(e) =>
                        setFormData({ ...formData, previewText: e.target.value })
                      }
                      placeholder="Texte affiché avant l'ouverture"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Couleur principale
                    </label>
                    <input
                      type="color"
                      value={formData.primaryColor ?? '#4F46E5'}
                      onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                      className="w-full h-10 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Couleur secondaire
                    </label>
                    <input
                      type="color"
                      value={formData.secondaryColor ?? '#7C3AED'}
                      onChange={(e) =>
                        setFormData({ ...formData, secondaryColor: e.target.value })
                      }
                      className="w-full h-10 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pied de page
                    </label>
                    <input
                      value={formData.footerText ?? ''}
                      onChange={(e) => setFormData({ ...formData, footerText: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-200 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowFormModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                {editingId ? 'Enregistrer' : 'Créer le template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Aperçu : {previewing.name}</h2>
              <button
                onClick={() => setPreviewing(null)}
                className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-2 pb-2 border-b border-gray-100">
                  <strong>Objet :</strong>{' '}
                  {renderTemplate(previewing.subject, {
                    lead: SAMPLE_LEAD,
                    unsubscribeUrl: '#',
                  })}
                </p>
                <div
                  className="prose prose-sm max-w-none bg-white rounded p-4"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: renderTemplate(previewing.content, {
                      lead: SAMPLE_LEAD,
                      unsubscribeUrl: '#',
                    }),
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <CuteConfirmModal
        isOpen={!!deleteTarget}
        title="Supprimer ce template"
        message="Le template sera retiré. Les campagnes qui s'en servent doivent être détachées avant suppression."
        itemName={deleteTarget?.name}
        confirmLabel="Supprimer"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default EmailTemplatesPage;
